import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiPatch, SUPER_TOKEN_KEY } from '../../api/client';
import { DEFAULT_ORG_TIME_ZONE, ORG_TIME_ZONES } from '../../lib/orgTimeZones';
import { superOrgBtn, superOrgField, superOrgInput } from './superOrgPanels';
import type { SuperOrgDetailOutletContext } from './SuperOrgDetailLayout';

export function SuperOrgBrandPage() {
  const { org, token, reload, setPageErr } = useOutletContext<SuperOrgDetailOutletContext>();
  const [logoUrl, setLogoUrl] = useState(org.logoUrl ?? null);
  const [timeZone, setTimeZone] = useState(org.timeZone || DEFAULT_ORG_TIME_ZONE);
  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState('');

  useEffect(() => {
    setLogoUrl(org.logoUrl ?? null);
  }, [org.logoUrl]);

  useEffect(() => {
    setTimeZone(org.timeZone || DEFAULT_ORG_TIME_ZONE);
  }, [org.timeZone]);

  const upload = useCallback(
    async (file: File | null) => {
      if (!file) return;
      setPageErr('');
      setOkMsg('');
      setBusy(true);
      try {
        const form = new FormData();
        form.append('file', file);
        const r = await fetch(`/api/super/organizations/${org.id}/brand-logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token || localStorage.getItem(SUPER_TOKEN_KEY) || ''}` },
          body: form,
        });
        const j = (await r.json()) as {
          success?: boolean;
          data?: { logoUrl?: string };
          error?: { message?: string };
        };
        if (!r.ok || !j.success) {
          throw new Error(j.error?.message ?? `HTTP ${r.status}`);
        }
        setLogoUrl(j.data?.logoUrl ?? null);
        setOkMsg('Logo actualizado. Se usa en la consola y en la app móvil de la empresa.');
        await reload();
      } catch (ex) {
        setPageErr((ex as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [org.id, token, reload, setPageErr]
  );

  const saveTimeZone = useCallback(async () => {
    setPageErr('');
    setOkMsg('');
    setBusy(true);
    try {
      await apiPatch(
        `/api/super/organizations/${org.id}`,
        { timeZone },
        token || localStorage.getItem(SUPER_TOKEN_KEY),
        { showErrorGrowl: false },
      );
      setOkMsg('Uso horario guardado. El agente saluda con la hora de esa zona.');
      await reload();
    } catch (ex) {
      setPageErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }, [org.id, timeZone, token, reload, setPageErr]);

  const remove = useCallback(async () => {
    setPageErr('');
    setOkMsg('');
    setBusy(true);
    try {
      const r = await fetch(`/api/super/organizations/${org.id}/brand-logo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token || localStorage.getItem(SUPER_TOKEN_KEY) || ''}` },
      });
      const j = (await r.json()) as { success?: boolean; error?: { message?: string } };
      if (!r.ok || !j.success) {
        throw new Error(j.error?.message ?? `HTTP ${r.status}`);
      }
      setLogoUrl(null);
      setOkMsg('Logo eliminado. Se vuelve a mostrar el de Interactive Talker.');
      await reload();
    } catch (ex) {
      setPageErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }, [org.id, token, reload, setPageErr]);

  return (
    <section>
      <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#0f172a' }}>Marca</h2>
      <p style={{ margin: '0 0 20px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
        Logo de la empresa para el header de la consola, la PWA y el ícono en pantalla (cuando la sesión
        esté activa). PNG, JPEG, WebP o SVG · máx. 1 MB. Cuadrado recomendado.
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 20,
          alignItems: 'flex-start',
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <img
            src={logoUrl || '/logo.png'}
            alt=""
            width={72}
            height={72}
            style={{ objectFit: 'contain', display: 'block' }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={superOrgField}>
            Archivo
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                e.target.value = '';
                void upload(f);
              }}
            />
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            <button type="button" style={superOrgBtn} disabled={busy || !logoUrl} onClick={() => void remove()}>
              {busy ? '…' : 'Quitar logo'}
            </button>
          </div>
          {okMsg ? <p style={{ color: '#047857', fontSize: 13, marginTop: 12 }}>{okMsg}</p> : null}
        </div>
      </div>

      <div style={{ maxWidth: 360 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Uso horario</h3>
        <p style={{ margin: '0 0 12px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
          El agente recibe la hora de esta zona en cada mensaje, para saludar según el momento del día del cliente.
        </p>
        <label style={superOrgField}>
          Zona
          <select
            value={timeZone}
            disabled={busy}
            onChange={(event) => setTimeZone(event.target.value)}
            style={{ ...superOrgInput, width: '100%' }}
          >
            {ORG_TIME_ZONES.some((zone) => zone.id === timeZone) ? null : (
              <option value={timeZone}>{timeZone}</option>
            )}
            {ORG_TIME_ZONES.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {zone.label}
              </option>
            ))}
          </select>
        </label>
        <button type="button" style={{ ...superOrgBtn, marginTop: 12 }} disabled={busy} onClick={() => void saveTimeZone()}>
          {busy ? 'Guardando…' : 'Guardar uso horario'}
        </button>
      </div>
    </section>
  );
}
